import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { adapterCalqueAuRatioImage, calculerZoneImageUtile, tailleTexteCalque } from './boutiqueClubPersonnalisationUtils';

export default function BoutiqueClubMarquageCanvas({
  className = '', image, imageAlt, visuel, marquages = [], options = [], valeurs = {},
  urlsPhotos = {}, polices = [], containerRef, stageRef, interactive = false,
  selectedId = null, onLayerSelect, onLayerPointerDown, onResizePointerDown, onImageNaturalSize, emptyContent, style,
  fallbackImage = ''
}) {
  const outerRef = useRef(null);
  const imageRef = useRef(null);
  const [zone, setZone] = useState({ left:0, top:0, width:100, height:100 });
  const [ratiosImages, setRatiosImages] = useState({});
  const [ratioRepere, setRatioRepere] = useState(0.78);
  const ratiosNotifies = useRef({});
  const assignOuter = useCallback((node) => {
    outerRef.current = node;
    if (typeof containerRef === 'function') containerRef(node);
    else if (containerRef) containerRef.current = node;
  }, [containerRef]);
  const assignStage = useCallback((node) => {
    if (typeof stageRef === 'function') stageRef(node);
    else if (stageRef) stageRef.current = node;
  }, [stageRef]);
  const mesurer = useCallback(() => {
    const outer = outerRef.current, img = imageRef.current;
    if (!outer || !img?.naturalWidth || !img?.naturalHeight) return;
    setZone(calculerZoneImageUtile(outer.clientWidth, outer.clientHeight, img.naturalWidth, img.naturalHeight));
    setRatioRepere(img.naturalWidth / img.naturalHeight);
  }, []);
  useLayoutEffect(() => {
    mesurer();
    const outer = outerRef.current;
    if (!outer || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(mesurer);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [image, mesurer]);
  const calques = marquages
    .filter((m) => m.visuel_id === visuel?.id && m.visible !== false)
    .sort((a, b) => Number(a.ordre_z || 0) - Number(b.ordre_z || 0));
  const texte = (m) => m.texte_fixe || valeurs[m.option_id] || options.find((o) => o.id === m.option_id)?.libelle || m.libelle;
  useLayoutEffect(() => {
    if (!onImageNaturalSize || !Number.isFinite(ratioRepere) || ratioRepere <= 0) return;
    calques.filter((m)=>m.type==='IMAGE_FIXE' && ratiosImages[m.id]).forEach((m)=>{
      const dimensions={...ratiosImages[m.id],ratioRepere};
      const cle=`${dimensions.width}x${dimensions.height}@${ratioRepere}`;
      if(ratiosNotifies.current[m.id]===cle)return;
      ratiosNotifies.current[m.id]=cle;
      onImageNaturalSize(m,dimensions);
    });
  },[calques,onImageNaturalSize,ratioRepere,ratiosImages]);

  return <div ref={assignOuter} className={`bc-personnalisation-canvas ${className}`.trim()} style={style}>
    {image ? <img ref={imageRef} className="bc-personnalisation-base" src={image} alt={imageAlt} loading={interactive?'eager':'lazy'} decoding="async" onLoad={mesurer}
      onError={(e)=>{if(fallbackImage&&e.currentTarget.getAttribute('src')!==fallbackImage)e.currentTarget.src=fallbackImage;}}/> : emptyContent}
    <div ref={assignStage} className="bc-personnalisation-image-stage" style={{left:`${zone.left}%`,top:`${zone.top}%`,width:`${zone.width}%`,height:`${zone.height}%`}}>
    {calques.map((m) => {
      const dimensionsNaturelles = ratiosImages[m.id];
      const calque = m.type === 'IMAGE_FIXE' && dimensionsNaturelles
        ? adapterCalqueAuRatioImage(m, dimensionsNaturelles.width, dimensionsNaturelles.height, ratioRepere)
        : m;
      const valeur = texte(m);
      const famille = polices.find((p) => p.code === m.police_code)?.famille_css;
      const style = {
        left:`${calque.position_x}%`, top:`${calque.position_y}%`, width:`${calque.largeur}%`, height:`${calque.hauteur}%`,
        zIndex:m.ordre_z, transform:`rotate(${m.rotation || 0}deg)`, color:m.couleur || '#fff',
        fontFamily:famille, fontSize:m.type === 'IMAGE_FIXE' ? undefined : tailleTexteCalque(m, valeur),
        lineHeight:1, whiteSpace:'nowrap', background:'transparent', backgroundColor:'transparent',
        backgroundImage:'none', boxShadow:'none', borderRadius:0
      };
      const contenu = m.type === 'IMAGE_FIXE' && urlsPhotos[m.storage_path]
        ? <img src={urlsPhotos[m.storage_path]} alt="" loading={interactive?'eager':'lazy'} decoding="async" style={{background:'transparent',backgroundColor:'transparent',objectFit:'contain'}} onLoad={(e)=>{const dimensions={width:e.currentTarget.naturalWidth,height:e.currentTarget.naturalHeight};setRatiosImages((r)=>r[m.id]?.width===dimensions.width&&r[m.id]?.height===dimensions.height?r:{...r,[m.id]:dimensions})}}/> : valeur;
      return interactive
        ? <button type="button" aria-label={`Calque ${m.libelle}`} data-layer-type={m.type} key={m.id}
            className={`bc-personnalisation-layer bcp-layer ${selectedId === m.id ? 'selected' : ''}`} style={style}
            onFocus={() => onLayerSelect?.(m.id)} onClick={() => onLayerSelect?.(m.id)}
            onPointerDown={(e) => { onLayerSelect?.(m.id); onLayerPointerDown?.(e,m); }}>
            {contenu}<i onPointerDown={(e) => { e.stopPropagation(); onLayerSelect?.(m.id); onResizePointerDown?.(e,m); }}>↘</i>
          </button>
        : <span key={m.id} data-testid={`rendu-calque-${m.id}`} data-layer-type={m.type} className="bc-personnalisation-layer" style={style}>{contenu}</span>;
    })}
    </div>
  </div>;
}
